import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSave, FiLoader, FiEdit3, FiMapPin, FiTrash2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GolfClubAutocomplete from './GolfClubAutocomplete';
import { updateGroupName, updateGroupHomeCourse } from '../services/groupService';

function EditGroupModal({ isOpen, onClose, group, onUpdateSuccess, onDeleteGroup }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    homeCourse: null
  });

  // Initialize form data when group changes or modal opens
  useEffect(() => {
    if (group && isOpen) {
      setFormData({
        name: group.name || '',
        homeCourse: group.home_course || null
      });
      setError('');
    }
  }, [group, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleHomeCourseChange = (course) => {
    setFormData(prev => ({
      ...prev,
      homeCourse: course
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.name?.trim()) {
      setError('Group name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (formData.name.trim() !== group.name) {
        await updateGroupName(group.id, formData.name.trim());
      }

      const currentHomeCourse = group.home_course;
      const newHomeCourse = formData.homeCourse;

      const homeCoursesEqual =
        (!currentHomeCourse && !newHomeCourse) ||
        (currentHomeCourse && newHomeCourse &&
         currentHomeCourse.id === newHomeCourse.id);

      if (!homeCoursesEqual) {
        await updateGroupHomeCourse(group.id, newHomeCourse);
      }

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      handleClose();
    } catch (err) {
      console.error('❌ Error updating group:', err);
      setError(err.message || 'Failed to update group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-surface border border-border-subtle rounded-lg shadow-card max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-text-primary">Edit Group</h2>
              <button
                onClick={handleClose}
                disabled={loading}
                className="w-10 h-10 rounded-md flex items-center justify-center hover:bg-app transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  <SafeIcon icon={FiEdit3} className="w-4 h-4 inline mr-2" />
                  Group Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="Enter group name"
                  className="w-full px-4 py-3 text-base border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted disabled:bg-app"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 inline mr-2" />
                  Home Course (Optional)
                </label>
                <GolfClubAutocomplete
                  selectedCourse={formData.homeCourse}
                  onCourseSelect={handleHomeCourseChange}
                  placeholder="Search for your group's home golf course..."
                  disabled={loading}
                  className="w-full"
                />
                <p className="text-xs text-text-muted mt-2">
                  Setting a home course will automatically suggest it when scheduling rounds
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading || !formData.name?.trim()}
                  className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
                      Update Group
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-app transition-colors disabled:opacity-50 font-medium"
                >
                  Cancel
                </button>
              </div>

              {onDeleteGroup && group?.isOwner && (
                <div className="border-t border-border-subtle pt-4 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete "${group.name}"? This action cannot be undone and will remove all members and scheduled rounds.`)) {
                        onDeleteGroup();
                        handleClose();
                      }
                    }}
                    disabled={loading}
                    className="w-full flex items-center justify-center min-h-[44px] px-4 py-2 text-sm font-medium text-status-error rounded-md hover:bg-status-error/10 transition-colors disabled:opacity-50"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
                    Delete Group
                  </button>
                </div>
              )}
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default EditGroupModal;
